import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { t } from '../i18n';

/**
 * Lire un billet a la camera.
 *
 * Pas de greffon natif : la vue web sait deja ouvrir la camera arriere —
 * c'est la meme permission que les appels video — et le decodage se fait
 * en JavaScript sur une image tiree du flux. Un greffon aurait demande
 * l'enregistrement natif qu'on connait, pour un gain nul a cette echelle.
 *
 * L'image analysee est reduite : un carre se lit tres bien en 400 points
 * de large, et decoder une image de telephone entiere ferait chuter la
 * cadence sous le seuil ou l'on sent le retard.
 */

/** Au-dela, on considere que le meme carre a ete relu par inadvertance. */
const MEME_CODE_MS = 2500;
const ANALYSE_LARGEUR = 400;

export interface Scanner {
  /** A poser sur la balise video : c'est elle qui montre la camera. */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Message en francais si la camera n'a pas pu s'ouvrir. */
  erreur: string | null;
  pret: boolean;
}

export function useScanner(actif: boolean, onCode: (texte: string) => void): Scanner {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [pret, setPret] = useState(false);

  /* Le rappel est fige a l'ouverture de la camera : le lire par reference
     evite de rouvrir le flux a chaque rendu du parent. */
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    if (!actif) return;

    let flux: MediaStream | null = null;
    let image = 0;
    let vivant = true;
    let dernier = '';
    let dernierA = 0;

    const canevas = document.createElement('canvas');
    const pinceau = canevas.getContext('2d', { willReadFrequently: true });

    function analyser() {
      if (!vivant) return;
      image = requestAnimationFrame(analyser);

      const video = videoRef.current;
      if (!pinceau || !video || video.readyState < 2 || !video.videoWidth) return;

      const echelle = ANALYSE_LARGEUR / video.videoWidth;
      canevas.width = ANALYSE_LARGEUR;
      canevas.height = Math.round(video.videoHeight * echelle);
      pinceau.drawImage(video, 0, 0, canevas.width, canevas.height);

      const pixels = pinceau.getImageData(0, 0, canevas.width, canevas.height);
      const trouve = jsQR(pixels.data, pixels.width, pixels.height, {
        inversionAttempts: 'dontInvert',
      });
      if (!trouve?.data) return;

      /*
        Le meme carre reste dans le champ tant que la personne n'a pas
        baisse son telephone : sans ce delai, il serait relu vingt fois
        par seconde et l'ecran clignoterait de confirmations.
      */
      const maintenant = Date.now();
      if (trouve.data === dernier && maintenant - dernierA < MEME_CODE_MS) return;
      dernier = trouve.data;
      dernierA = maintenant;
      onCodeRef.current(trouve.data);
    }

    void (async () => {
      try {
        flux = await navigator.mediaDevices.getUserMedia({
          // La camera arriere : on vise le telephone de quelqu'un d'autre.
          video: { facingMode: { ideal: 'environment' } },
        });
        if (!vivant) {
          for (const piste of flux.getTracks()) piste.stop();
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = flux;
          await videoRef.current.play().catch(() => undefined);
        }
        setPret(true);
        image = requestAnimationFrame(analyser);
      } catch {
        setErreur(t.composants.scanner.sansCamera);
      }
    })();

    return () => {
      vivant = false;
      cancelAnimationFrame(image);
      setPret(false);
      // La camera doit se refermer avec l'ecran : la laisser ouverte
      // allume la pastille de l'iPhone pour rien.
      for (const piste of flux?.getTracks() ?? []) piste.stop();
    };
  }, [actif]);

  return { videoRef, erreur, pret };
}
